import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';

import {
    Typography, Button, Slider,
} from '@material-ui/core';

import { hasValue } from '../../../objectUtils';
import { with1dArrowNavigation } from '../../accessibility/ArrowNavigation';

const styles = theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column-reverse', // labels are positioned first but displayed last. To help for screen readers
        flexWrap: 'wrap',
        border: theme.youwell.borders.inputBox,
        backgroundColor: theme.youwell.colors.inputBackground,
        padding: theme.spacing(2),

        '&$vertical': {
            height: 400,
            flexDirection: 'column',
        },
    },
    vertical: {
    },
    scale: {
        width: '100%',
        padding: theme.spacing(3, 0, 2),

        '$vertical &': {
            width: '50%',
            height: '100%',
        },
    },
    divider: {
        flex: '1 1 auto',
        margin: theme.spacing(1),
        borderBottom: theme.youwell.borders.normal,

        [theme.breakpoints.down(700)]: {
            margin: 4,
        },
        [theme.breakpoints.down(650)]: {
            margin: 2,
        },
        [theme.breakpoints.down(620)]: {
            margin: 0,
        },
        [theme.breakpoints.down('xs')]: {
            border: 0,
            margin: 0,
        },
    },
    dividerSmall: {
        margin: theme.spacing(0.5),

        [theme.breakpoints.down('xs')]: {
            margin: 0,
        },
    },
    noLine: {
        borderBottom: 'none',
    },
    step: {
        width: '100%',
        minWidth: 0,
        maxWidth: 64,
        wordBreak: 'normal', // override setting from TaskElement

        [theme.breakpoints.down('xs')]: {
            maxWidth: 48,
        },
    },
    buttons: {
        width: '100%',
        padding: theme.spacing(3, 0, 2),
        display: 'flex',
        alignItems: 'center',

        '$vertical &': {
            width: '50%',
            height: '100%',
        },
    },
    smallButtons: {
    },
    scaleLabels: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',

        '$vertical &': {
            padding: theme.spacing(1, 0),
            textAlign: 'right',
            width: '50%',
            flexDirection: 'column-reverse',
            alignItems: 'flex-end',
            height: '100%',
        },
    },
    label: {
        flex: '1 1 30%',

        '$vertical &': {
            flex: '0 0 auto',
        },
    },
    lastLabel: {
        marginLeft: theme.spacing(1),
        textAlign: 'right',
    },
    slider: {
        margin: theme.spacing(0, 1, 2),
        width: 'calc(100% - 16px)',
    },
    showThumb: {
        display: 'block',
    },
    hideThumb: {
        display: 'none',
    },
});

class ScaleResponse extends React.Component {
    state = {
        sliderValue: undefined,
    }

    handleChange = value => () => {
        if (this.props.readOnly) return;

        this.props.handleResponseUpdate(value);
    }

    handleSliderChange = (event, value) => {
        if (this.props.readOnly) return;

        this.setState({ sliderValue: value }, () => {
            if (this.updateTimeout) {
                clearTimeout(this.updateTimeout);
            }
            this.updateTimeout = setTimeout(this.updateSliderValue, 500);
        });
    };

    updateSliderValue = () => {
        this.props.handleResponseUpdate(this.state.sliderValue);
    }

    renderLabels = (scaleConfig, classes) => {
        if (scaleConfig.minLabel && scaleConfig.maxLabel) {
            return (
                <div className={classes.scaleLabels}>
                    <Typography color="primary" className={classes.label}>
                        {scaleConfig.minLabel}
                    </Typography>
                    <Typography color="primary" className={classes.label} align="center">
                        {scaleConfig.midLabel}
                    </Typography>
                    <Typography color="primary" className={classnames(classes.label, classes.lastLabel)}>
                        {scaleConfig.maxLabel}
                    </Typography>
                </div>
            );
        }
        return null;
    }

    render() {
        const {
            classes, scaleConfig, response, disabled, navigation,
        } = this.props;

        const range = Math.abs(scaleConfig.max - scaleConfig.min);

        if (range >= 10) {
            const sliderValue = hasValue(this.state.sliderValue) ? this.state.sliderValue : response;
            const rangeStep = Math.ceil(range / 5);
            const marks = [];
            marks.push({ value: scaleConfig.min, label: scaleConfig.min });
            for (let value = rangeStep; value < scaleConfig.max; value += rangeStep) {
                marks.push({ value, label: value });
            }
            marks.push({ value: scaleConfig.max, label: scaleConfig.max });
            return (
                <div className={classnames(classes.root, { [classes.vertical]: scaleConfig.vertical })}>
                    {this.renderLabels(scaleConfig, classes)}

                    <div className={classes.scale}>
                        <Slider
                            className={classes.slider}
                            classes={{ thumb: hasValue(sliderValue) ? classes.showThumb : classes.hideThumb }}
                            value={sliderValue || scaleConfig.min}
                            onChange={this.handleSliderChange}
                            marks={marks}
                            valueLabelDisplay="on"
                            min={scaleConfig.min}
                            max={scaleConfig.max}
                            step={scaleConfig.step}
                            disabled={disabled}
                            orientation={scaleConfig.vertical ? 'vertical' : 'horizontal'}
                        />
                    </div>
                </div>
            );
        }

        const steps = [];
        for (let i = scaleConfig.min; i <= scaleConfig.max; i += scaleConfig.step) {
            steps.push(i);
        }

        const smallButtons = steps.length > 5;

        return (
            <div className={classes.root}>
                {this.renderLabels(scaleConfig, classes)}

                <div className={classes.buttons}>
                    {steps.map((step, index) => (
                        <React.Fragment key={step}>
                            {index > 0 && <div className={classnames(classes.divider, { [classes.noLine]: steps[0] < 0, [classes.dividerSmall]: smallButtons })} />}
                            <Button
                                className={classnames(classes.step)}
                                variant={step === response ? 'contained' : 'outlined'}
                                color="primary"
                                aria-label={step}
                                onClick={this.handleChange(step)}
                                disabled={disabled}
                                size={smallButtons ? 'small' : 'medium'}
                                {...{
                                    ref: navigation.setRef(index),
                                    tabIndex: -1,
                                    onKeyDown: navigation.handleKeyDown,
                                    onMouseDown: navigation.handleMouseDown(index),
                                    ...index === 0 && {
                                        tabIndex: 0,
                                        onFocus: navigation.handleMouseDown(index),
                                    },
                                }}
                            >
                                {step}
                            </Button>
                        </React.Fragment>
                    ))}
                </div>
            </div>
        );
    }
}

ScaleResponse.propTypes = {
    navigation: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    handleResponseUpdate: PropTypes.func.isRequired,
    scaleConfig: PropTypes.object.isRequired,
    response: PropTypes.number,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
};

ScaleResponse.defaultProps = {
    response: null,
    disabled: false,
    readOnly: false,
};

export default withStyles(styles)(with1dArrowNavigation(ScaleResponse));
